import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../hooks/useAuth'
import { supabase } from '../lib/supabase'
import { Button } from '../components/ui/Button'
import { Input } from '../components/ui/Input'
import { CAREER_TREES } from '../lib/stripe'
import './Settings.css'

export function Settings() {
  const { user, profile, refreshProfile, signOut } = useAuth()
  const navigate = useNavigate()

  const [saving,   setSaving  ] = useState(false)
  const [saved,    setSaved   ] = useState(false)
  const [fullName, setFullName] = useState(profile?.full_name || '')
  const [error,    setError   ] = useState('')

  const [showCancelConfirm, setShowCancelConfirm] = useState(false)
  const [cancelling,        setCancelling        ] = useState(false)
  const [cancelError,       setCancelError       ] = useState('')
  const [cancelDone,        setCancelDone        ] = useState(false)

  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [deleteText,        setDeleteText        ] = useState('')
  const [deleting,          setDeleting          ] = useState(false)
  const [deleteError,       setDeleteError       ] = useState('')

  const treeInfo          = CAREER_TREES.find(t => t.id === profile?.selected_tree)
  const isActive          = profile?.subscription_status === 'active'
  const isPendingCancel   = isActive && profile?.cancel_at_period_end === true
  const isCancelled       = ['cancelled', 'inactive'].includes(profile?.subscription_status)
  const canDelete         = isCancelled || isPendingCancel || !profile?.subscription_tier

  const accessUntil = profile?.subscription_end
    ? new Date(profile.subscription_end).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })
    : null

  async function handleSaveName(e) {
    e.preventDefault()
    if (!fullName.trim()) return
    setSaving(true); setError('')

    try {
      const { error: sbErr } = await supabase
        .from('profiles').update({ full_name: fullName.trim() }).eq('id', user.id)
      if (sbErr) {
        setError('Failed to save. Try again.')
      } else {
        await refreshProfile()
        setSaved(true)
        setTimeout(() => setSaved(false), 2500)
      }
    } catch (e) {
      console.error('Settings: name save threw:', e)
      setError('Network error. Check your connection and try again.')
    } finally {
      setSaving(false)
    }
  }

  async function handleCancel() {
    setCancelling(true); setCancelError('')
    try {
      const { data: { session } } = await supabase.auth.getSession()
      const res = await fetch('/.netlify/functions/manage-subscription', {
        method:  'POST',
        headers: {
          'Content-Type':  'application/json',
          'Authorization': `Bearer ${session?.access_token}`,
        },
        body: JSON.stringify({ action: 'cancel', userId: user.id }),
      })
      const data = await res.json()
      if (!res.ok) throw new Error(data.error || 'Failed to cancel')
      await refreshProfile()
      setCancelDone(true)
      setShowCancelConfirm(false)
    } catch (err) {
      setCancelError(err.message || 'Something went wrong. Email hello@techbench.it to cancel manually.')
    } finally {
      setCancelling(false)
    }
  }

  async function handleDelete() {
    if (deleteText !== 'DELETE') return
    setDeleting(true); setDeleteError('')
    try {
      const { data: { session } } = await supabase.auth.getSession()
      const res = await fetch('/.netlify/functions/delete-account', {
        method:  'POST',
        headers: {
          'Content-Type':  'application/json',
          'Authorization': `Bearer ${session?.access_token}`,
        },
        body: JSON.stringify({ userId: user.id }),
      })
      const data = await res.json()
      if (!res.ok) throw new Error(data.error || 'Failed to delete account')
      await signOut()
      navigate('/', { replace: true })
    } catch (err) {
      setDeleteError(err.message || 'Something went wrong. Email hello@techbench.it to delete your account.')
      setDeleting(false)
    }
  }

  return (
    <div className="settings">
      <h1 className="settings__title">Settings</h1>

      {/* Account */}
      <section className="settings-section">
        <h2 className="settings-section__heading">Account</h2>
        <div className="settings-field-row">
          <div className="settings-label">Email</div>
          <div className="settings-value mono">{user?.email}</div>
        </div>
        <form className="settings-name-form" onSubmit={handleSaveName}>
          <Input
            id="fullName" label="Display name"
            value={fullName}
            onChange={e => { setFullName(e.target.value); setSaved(false) }}
            error={error}
          />
          <Button type="submit" variant="secondary" loading={saving} size="sm">
            {saved ? 'Saved ✓' : 'Save'}
          </Button>
        </form>
      </section>

      {/* Subscription */}
      <section className="settings-section">
        <h2 className="settings-section__heading">Subscription</h2>
        <div className="settings-field-row">
          <div className="settings-label">Current tier</div>
          <div className="settings-value">
            <span className="settings-tier-badge">
              {(profile?.subscription_tier || 0) === 0
                ? 'No active subscription'
                : (() => {
                    const t = profile.subscription_tier
                    const name = ['', 'Foundation', 'Entry Level', 'Mid-Level', 'Senior'][t] || 'Foundation'
                    return `Tier ${t} — ${name}`
                  })()
              }
            </span>
          </div>
        </div>
        <div className="settings-field-row">
          <div className="settings-label">Status</div>
          <div className={`settings-value settings-status settings-status--${profile?.subscription_status || 'inactive'}`}>
            {profile?.subscription_status || 'inactive'}
          </div>
        </div>
        {treeInfo && (
          <div className="settings-field-row">
            <div className="settings-label">Career tree</div>
            <div className="settings-value">{treeInfo.label}</div>
          </div>
        )}

        {/* Cancel subscription */}
        {isActive && !isPendingCancel && !cancelDone && (
          <div className="settings-action-block">
            {!showCancelConfirm ? (
              <>
                <p className="settings-note">
                  Cancelling ends billing at the end of your current paid period.
                  Your account, progress, unlocks, and certificates are preserved permanently.
                  You can resubscribe at any time.
                </p>
                <div style={{ display: 'flex', justifyContent: 'center', marginTop: '12px' }}>
                  <Button variant="secondary" size="sm" onClick={() => setShowCancelConfirm(true)}>
                    Cancel subscription
                  </Button>
                </div>
              </>
            ) : (
              <div className="settings-confirm-box">
                <p className="settings-confirm-text">
                  Billing will stop at the end of your current period. You keep access until then.
                  Your progress and data are preserved — you can resubscribe any time.
                </p>
                {cancelError && <p className="settings-error">{cancelError}</p>}
                <div className="settings-confirm-actions">
                  <Button variant="secondary" size="sm" onClick={() => setShowCancelConfirm(false)} disabled={cancelling}>
                    Keep subscription
                  </Button>
                  <Button size="sm" loading={cancelling} onClick={handleCancel}>
                    Yes, cancel billing
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}

        {(isPendingCancel || cancelDone) && (
          <div className="settings-action-block">
            <p className="settings-note" style={{ color: 'var(--teal)' }}>
              ✓ Subscription cancelled.{' '}
              {accessUntil
                ? <>Access continues until <strong>{accessUntil}</strong>.</>
                : <>Access continues until the end of your current period.</>
              }{' '}
              Your progress and data are preserved.
            </p>
          </div>
        )}
      </section>

      {/* Sign out */}
      <section className="settings-section">
        <h2 className="settings-section__heading">Session</h2>
        <p className="settings-note">Your progress is saved. You can sign back in at any time.</p>
        <Button
          variant="secondary"
          size="sm"
          onClick={() => {
            signOut()
            navigate('/login', { replace: true })
          }}
        >
          Sign out
        </Button>
      </section>

      {/* Delete account */}
      <section className="settings-section settings-section--danger">
        <h2 className="settings-section__heading settings-section__heading--danger">Delete account</h2>
        {!canDelete ? (
          <>
            <p className="settings-note">
              You must cancel your subscription before deleting your account.
              This ensures billing stops before your data is removed.
            </p>
            <Button variant="secondary" size="sm" disabled>
              Delete account — cancel subscription first
            </Button>
          </>
        ) : !showDeleteConfirm ? (
          <>
            <p className="settings-note">
              Permanently deletes your account, all progress, certificates, and unlocks.
              This cannot be undone.
            </p>
            <Button variant="danger" size="sm" onClick={() => setShowDeleteConfirm(true)}>
              Delete my account
            </Button>
          </>
        ) : (
          <div className="settings-confirm-box settings-confirm-box--danger">
            <p className="settings-confirm-text">
              This will permanently delete your account and all data. There is no recovery.
              <br/><br/>
              Type <strong>DELETE</strong> to confirm.
            </p>
            <input
              type="text"
              className="settings-delete-input"
              placeholder="Type DELETE to confirm"
              value={deleteText}
              onChange={e => setDeleteText(e.target.value)}
              autoComplete="off"
            />
            {deleteError && <p className="settings-error">{deleteError}</p>}
            <div className="settings-confirm-actions">
              <Button variant="secondary" size="sm"
                onClick={() => { setShowDeleteConfirm(false); setDeleteText('') }}
                disabled={deleting}>
                Cancel
              </Button>
              <Button variant="danger" size="sm"
                disabled={deleteText !== 'DELETE' || deleting}
                loading={deleting}
                onClick={handleDelete}>
                Delete permanently
              </Button>
            </div>
          </div>
        )}
      </section>
    </div>
  )
}
